import React from "react";
import {
  Box,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Check, Close } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import Form from "../../forms/components/Form";
import Input from "../../forms/components/Input";
import ROUTES from "../../router/routesModel";
import FormLink from "../../forms/components/FormLink";
import legalContent from "../../legal/legalContent";
import { useDismissible } from "../../native/dismissibleStack";
import { SAFE_TOP } from "../../styles/safeArea";

const PasswordRule = ({ met, text }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
    {met ? (
      <Check sx={{ fontSize: 14, color: "success.main" }} />
    ) : (
      <Close sx={{ fontSize: 14, color: "text.disabled" }} />
    )}
    <Typography
      variant="caption"
      sx={{ color: met ? "success.main" : "text.secondary" }}
    >
      {text}
    </Typography>
  </Box>
);

const UserForm = ({
  onSubmit,
  onReset,
  hasFormErrors,
  title,
  errors,
  data,
  onInputChange,
  setData,
  // #50. Forwarded straight to Form so the page's in-flight flag reaches the
  // Submit control. SignUpPage is this component's ONLY consumer — the
  // settings edit path renders EditProfileSheet, which gates its own Save at
  // EditProfileSheet.jsx:174 and never touches Form.
  submitting = false,
}) => {
  const pw = data.password || "";
  const pwRules = [
    { met: pw.length >= 7, text: "At least 7 characters" },
    { met: /[A-Z]/.test(pw), text: "One uppercase letter" },
    { met: /[a-z]/.test(pw), text: "One lowercase letter" },
    { met: /\d/.test(pw), text: "One number" },
    { met: /[!@#$%^&*-]/.test(pw), text: "One special character (!@#$%^&*-)" },
  ];

  // P2-58. The consent row asks the visitor to accept two documents, so it has
  // to let them read those documents. Following the links as ordinary router
  // pushes unmounted this form, and every field lives in `useState` inside
  // SignUpPage's useForm — name, date of birth, email, the password that has to
  // satisfy the five live rules above, and the consent tick itself all went back
  // to blank. Reading the thing you are consenting to must not cost you the form.
  //
  // So the links open the same `legalContent` in a dialog over the form instead.
  // Nothing unmounts, so nothing is lost and nothing has to be persisted — no
  // half-typed credentials or PII parked in web storage, and no draft to expire
  // or leak to the next person on a shared device.
  //
  // They stay real `<a href="/terms">` router links: the /terms and /privacy
  // routes remain the canonical destination, a modified click (ctrl/cmd/middle)
  // still opens the full page in a new tab on the web, and the href survives for
  // anything that reads it. Only the plain left click — the one Android WebView
  // sends, where there is no new tab to open to — is intercepted.
  const [policyKey, setPolicyKey] = React.useState(null);
  const policyDoc = policyKey ? legalContent[policyKey] : null;
  // Both policies run to several thousand words. On a phone that is a full-screen
  // read, not a centred card with 32px of margin — same treatment EditProfileSheet
  // gives its own sheet.
  const theme = useTheme();
  const policyFullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const openPolicy = (key) => (e) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return;
    e.preventDefault();
    setPolicyKey(key);
  };
  const closePolicy = () => setPolicyKey(null);

  // ...and the same care has to extend to Android's hardware Back, which is the
  // universal reflex for closing a modal. Back is a single event with a single
  // winner, and an overlay only wins it by registering here: `handleBackPress`
  // asks `hasDismissible()` first, then for a focused text field. With the
  // dialog open neither was true — MUI traps focus on the dialog's paper, a
  // `div`, so the "blur the field" branch does not fire either — and /signup is
  // not a HOME_ROUTE, so Back fell through to `navigate(-1)` and unmounted the
  // form. That is the exact loss this whole block exists to prevent, arriving
  // on the one platform the note above names.
  useDismissible(Boolean(policyDoc), closePolicy, "signup-policy-dialog");

  return (
    <Form
      onSubmit={onSubmit}
      onReset={onReset}
      submitting={submitting}
      hasFormErrors={hasFormErrors}
      styles={{ maxWidth: "800px" }}
      title={title}
      to={ROUTES.CARDS}
    >
      {/* B-46. Autofill can offer the person's own name, and the keyboard
          does not "correct" an unusual one. */}
      <Input
        name="first"
        label="first name"
        autoComplete="given-name"
        noAutoCorrect
        error={errors.first}
        onInputChange={onInputChange}
        data={data}
        breakPoints={{ sm: 6 }}
      />
      <Input
        name="last"
        label="last name"
        autoComplete="family-name"
        noAutoCorrect
        error={errors.last}
        onInputChange={onInputChange}
        data={data}
        breakPoints={{ sm: 6 }}
      />
      {data.dateOfBirth !== undefined && (
        <Input
          name="dateOfBirth"
          label="Date of birth"
          type="date"
          error={errors.dateOfBirth}
          onInputChange={onInputChange}
          data={data}
          breakPoints={{ sm: 6 }}
        />
      )}
      {/* #109. The only production caller of this form is SignUpPage, so these
          two fields are unambiguously creating a credential. "email" matches the
          token LoginPage already uses for the same field, and "new-password"
          tells the manager to offer a generated password rather than fill an
          existing one. The name fields above carry their own tokens (B-46);
          the date of birth stays on Input's "off" default. */}
      <Input
        name="email"
        label="email"
        type="email"
        autoComplete="email"
        error={errors.email}
        onInputChange={onInputChange}
        data={data}
        breakPoints={{ sm: 6 }}
      />
      <Input
        name="password"
        label="password"
        type="password"
        autoComplete="new-password"
        error={errors.password}
        onInputChange={onInputChange}
        data={data}
        breakPoints={{ sm: 6 }}
      />
      {pw.length > 0 && (
        <Box sx={{ px: 1, pt: 0.5, pb: 1, width: "100%" }}>
          {pwRules.map((rule, i) => (
            <PasswordRule key={i} met={rule.met} text={rule.text} />
          ))}
        </Box>
      )}
      {/* #65. Consent is required at account creation. Rendered only when the
          form carries the field (signup), so the shared edit form is untouched.
          The policies stay in-app — never an external URL — so they also work
          inside the Capacitor wrap; see the P2-58 note above for how. */}
      {data.acceptedTerms !== undefined && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                name="acceptedTerms"
                checked={Boolean(data.acceptedTerms)}
                // The error line below is this box's description; without the
                // link TalkBack read only "not checked" (E2E audit 17·9).
                inputProps={{
                  "aria-invalid": errors.acceptedTerms ? true : undefined,
                  "aria-describedby": errors.acceptedTerms ? "signup-terms-error" : undefined,
                }}
                onChange={(e) =>
                  onInputChange({
                    target: { name: "acceptedTerms", value: e.target.checked },
                  })
                }
              />
            }
            label="I confirm I am 18 or older and agree to the Terms of Service and Privacy Policy."
          />
          <Box sx={{ pl: 4, mt: -0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Read the{" "}
              <Link
                component={RouterLink}
                to={ROUTES.TERMS}
                onClick={openPolicy("terms")}
              >
                Terms of Service
              </Link>{" "}
              and the{" "}
              <Link
                component={RouterLink}
                to={ROUTES.PRIVACY}
                onClick={openPolicy("privacy")}
              >
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>
          <Dialog
            open={Boolean(policyDoc)}
            onClose={closePolicy}
            scroll="paper"
            fullWidth
            maxWidth="md"
            fullScreen={policyFullScreen}
            aria-labelledby="policy-dialog-title"
          >
            {/* Full-screen on a phone: the title would start under the status
                bar, so the inset goes on top of MUI's 16px. */}
            <DialogTitle
              id="policy-dialog-title"
              style={policyFullScreen ? { paddingTop: `calc(16px + ${SAFE_TOP})` } : undefined}
            >
              {policyDoc?.title}
            </DialogTitle>
            <DialogContent dividers>
              {String(policyDoc?.body || "")
                .split(/\n{2,}/)
                .map((p) => p.trim())
                .filter(Boolean)
                .map((p, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 2, lineHeight: 1.7 }}>
                    {p}
                  </Typography>
                ))}
            </DialogContent>
            <DialogActions>
              <Button onClick={closePolicy}>Close</Button>
            </DialogActions>
          </Dialog>
          {errors.acceptedTerms && (
            <FormHelperText id="signup-terms-error" error sx={{ pl: 4 }}>
              {typeof errors.acceptedTerms === "string"
                ? errors.acceptedTerms
                : errors.acceptedTerms.message}
            </FormHelperText>
          )}
        </Grid>
      )}
      <FormLink text="Already registered?" to={ROUTES.LOGIN} />
    </Form>
  );
};

export default React.memo(UserForm);
